import { useAppAbility } from "@/casl";
import { Typography, Button, Loading, Icon, Tooltip, Divider } from "@/components/atoms";
import {
  useGetBookingLedgerQuery,
  useGetBookingPaymentBreakdownQuery,
  useGetBookingPaymentsMetaQuery,
  useGetBookingPaymentsQuery,
  useGetBookingRefundsQuery,
} from "@/redux/apis/booking/endpoints/payment";
import { useAppDispatch } from "@/redux/hooks";
import { setPaymentAction } from "@/redux/slices/booking/bookingSlice";
import { useSelectedBooking } from "../../hooks/useSelectedBooking";
import { EmptyBreakdown } from "./emptyStates/EmptyBreakdown";
import { EmptyLedger } from "./emptyStates/EmptyLedger";
import { CollectedTable } from "./tables/CollectedTable";
import { PaidOutTable } from "./tables/PaidOutTable";
import { PaymentLedgerTable } from "./tables/PaymentLedgerTable";
import { RefundActivityTable } from "./tables/RefundActivityTable";

export const PaymentLogs = () => {
  const ability = useAppAbility();
  const dispatch = useAppDispatch();
  const { id: bookingId } = useSelectedBooking();
  const { isFetching: isLoadingPayments, data: payments } = useGetBookingPaymentsQuery(bookingId);
  const { isFetching: isLoadingBreakdown, data: breakdown } = useGetBookingPaymentBreakdownQuery(bookingId);
  const { isFetching: isLoadingLedger, data: ledger } = useGetBookingLedgerQuery(bookingId);
  const { isFetching: isLoadingRefunds, data: refunds } = useGetBookingRefundsQuery(bookingId);
  const { isFetching: isLoadingMeta, data: meta } = useGetBookingPaymentsMetaQuery(bookingId);

  const isLoading = isLoadingPayments || isLoadingBreakdown || isLoadingLedger || isLoadingMeta || isLoadingRefunds;

  const collected = payments || [];
  const paidOut = breakdown?.filter((b) => !b.transferType.includes("Refund")) || [];
  const ledgerData = ledger?.filter((l) => !l.description.includes("Refund")) || [];
  const refundData = refunds || [];

  return (
    <section className="flex h-full w-[100vw] flex-col sm:w-full">
      <div className="flex w-full flex-1 flex-col gap-7 p-5">
        {isLoading && <Loading />}

        {collected.length > 0 && (
          <div>
            <Typography variant="title" className="block">
              Collected
            </Typography>
            <CollectedTable logs={collected} />
          </div>
        )}

        {refundData.length > 0 && (
          <div>
            <Typography variant="title" className="block">
              Refund Activity
            </Typography>
            <RefundActivityTable items={refundData} />
          </div>
        )}

        <div>
          <div className="rows flex items-center gap-x-1">
            <Typography variant="title" className="block">
              Paid Out
            </Typography>
            <Tooltip content="Any money paid to you can be viewed in the payout history" placement="right">
              <Icon name="InfoCircle" variant="Bold" size="sm" className="rotate-180 text-neutral-dark-gray" />
            </Tooltip>
          </div>
          {paidOut.length ? <PaidOutTable items={paidOut} /> : <EmptyBreakdown />}
        </div>

        {ability.can("manage", "faProvider") && (
          <div>
            <Typography variant="title" className="block">
              Ledger (RM Admin)
            </Typography>
            {ledgerData.length ? <PaymentLedgerTable items={ledgerData} /> : <EmptyLedger />}
          </div>
        )}
      </div>

      {meta?.isPrepaymentAvailable && (
        <div className="flex flex-col p-5">
          <Divider className="-mx-5" />
          <Button
            onClick={() => dispatch(setPaymentAction({ action: "take-prepayment" }))}
            className="mt-5 self-end"
            variant="primary"
            size="lg"
          >
            Take Trip Pre-Payment
          </Button>
        </div>
      )}
    </section>
  );
};
